import {
	Box,
	Button,
	Collapse,
	Divider,
	HStack,
	Icon,
	Input,
	InputGroup,
	InputLeftElement,
	MenuItem,
	MenuList,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Spinner,
	Text,
	Textarea,
	VStack,
	useDisclosure,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import React, { useMemo, useRef, useState } from "react";
import { FaStar } from "react-icons/fa";
import { FaClipboardList } from "react-icons/fa6";
import { GiBrain } from "react-icons/gi";
import { MdExpandLess, MdExpandMore, MdLock, MdSchedule, MdUndo, MdWarning } from "react-icons/md";
import { AnalystProto } from "build-ts/analyst";
import { Header } from "build-ts/metadata";
import { getAnalyst, updateAnalyst } from "services/AnalystService";
import { listScheduledJobs } from "services/ScheduleServices";
import { ScheduledJobProto } from "build-ts/schedule";

interface ChatAddMenuComponentProps {
	analystId: string;
	chatId: string;
	scenarioJobs?: ScheduledJobProto[];
	onSelectAnalysis?: (message: string) => void;
}

const ChatAddMenuComponent: React.FC<ChatAddMenuComponentProps> = ({ analystId, chatId, scenarioJobs, onSelectAnalysis }) => {
	// ── Learn modal state ─────────────────────────────────────────────────────
	const { isOpen: isLearnOpen, onOpen: onLearnOpen, onClose: onLearnClose } = useDisclosure();
	const [analyst, setAnalyst] = useState<AnalystProto | null>(null);
	const [existingInstructions, setExistingInstructions] = useState<string>("");
	const [isEditingExisting, setIsEditingExisting] = useState(false);
	const [isExistingOpen, setIsExistingOpen] = useState(false);
	const [editedExisting, setEditedExisting] = useState<string>("");
	const [newInstruction, setNewInstruction] = useState<string>("");
	const [isFetchingAnalyst, setIsFetchingAnalyst] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const newInstructionRef = useRef<HTMLTextAreaElement>(null);

	// ── Analysis modal state ──────────────────────────────────────────────────
	const { isOpen: isAnalysisOpen, onOpen: onAnalysisOpen, onClose: onAnalysisClose } = useDisclosure();
	const [jobs, setJobs] = useState<Header[]>([]);
	const [isFetchingJobs, setIsFetchingJobs] = useState(false);
	const [jobSearch, setJobSearch] = useState("");

	const filteredJobs = useMemo(() => {
		const term = jobSearch.toLowerCase().trim();
		if (!term) return jobs;
		return jobs.filter(j => j.name?.toLowerCase().includes(term));
	}, [jobs, jobSearch]);

	// ── Learn handlers ────────────────────────────────────────────────────────
	const handleLearnClick = async () => {
		if (!analystId) return;
		setIsFetchingAnalyst(true);
		const fetched = await getAnalyst(analystId);
		setIsFetchingAnalyst(false);
		if (!fetched) return;
		setAnalyst(fetched);
		const existing = fetched.data_scope?.instructions ?? "";
		setExistingInstructions(existing);
		setEditedExisting(existing);
		setIsEditingExisting(false);
		setIsExistingOpen(false);
		setNewInstruction("");
		onLearnOpen();
	};

	const handleSave = async () => {
		if (!analyst) return;
		setIsSaving(true);
		const base = isEditingExisting ? editedExisting.trim() : existingInstructions.trim();
		const appended = newInstruction.trim();
		const combined = base && appended
			? `${base}\n\n${appended}`
			: base || appended;
		const updated: AnalystProto = {
			...analyst,
			data_scope: {
				...(analyst.data_scope ?? { metrics: [], benchmarks: [], column_scopes: [] }),
				instructions: combined,
			},
		};
		const result = await updateAnalyst(updated, true);
		setIsSaving(false);
		if (result) {
			setAnalyst(result);
			onLearnClose();
			window.open("/chat/new", "_blank");
		}
	};

	const handleLearnClose = () => {
		if (!isSaving) onLearnClose();
	};

	// ── Analysis handlers ─────────────────────────────────────────────────────
	const handleAnalysisClick = async () => {
		if (!analystId) return;
		if (scenarioJobs && scenarioJobs.length > 0) {
			setJobs(scenarioJobs.map(job => Header.fromJSON(job.header ?? {})));
			setJobSearch("");
			onAnalysisOpen();
			return;
		}
		setIsFetchingJobs(true);
		const response = await listScheduledJobs(
			"SCENARIO_JOB",
			-1,
			1,
			analystId,
			"LAST_MODIFIED",
			false,
			true,  /* includeScheduleActivity */
			"",    /* activeSchedule */
		);
		setIsFetchingJobs(false);
		setJobs(response ?? []);
		setJobSearch("");
		onAnalysisOpen();
	};

	const handleJobSelect = (job: Header) => {
		const name = job.name?.trim() || "Untitled";
		onSelectAnalysis?.(`[[analysis: ${name}]]`);
		onAnalysisClose();
	};

	return (
		<>
			<MenuList minW="240px" py={0} zIndex={1500}>
				{/* Learn */}
				<MenuItem
					py={3}
					px={3}
					_hover={{ bg: "purple.50" }}
					_focus={{ bg: "purple.50" }}
					onClick={handleLearnClick}
					isDisabled={isFetchingAnalyst || !analystId}
				>
					<VStack align="stretch" spacing={1} w="100%">
						<HStack spacing={2}>
							{isFetchingAnalyst ? (
								<Spinner size="xs" color="purple.600" />
							) : (
								<Icon as={GiBrain} boxSize={5} color="purple.600" />
							)}
							<Text fontWeight="600" fontSize="sm" m={0}>Learn</Text>
						</HStack>
						<Text fontSize="xs" color="gray.600" m={0}>Teach Anko a nuance</Text>
					</VStack>
				</MenuItem>

				{/* Analysis */}
				<MenuItem
					py={3}
					px={3}
					_hover={{ bg: "purple.50" }}
					_focus={{ bg: "purple.50" }}
					onClick={handleAnalysisClick}
					isDisabled={isFetchingJobs || !analystId}
				>
					<VStack align="stretch" spacing={1} w="100%">
						<HStack spacing={2}>
							{isFetchingJobs ? (
								<Spinner size="xs" color="purple.600" />
							) : (
								<Icon as={FaClipboardList} boxSize={5} color="purple.600" />
							)}
							<Text fontWeight="600" fontSize="sm" m={0}>Analysis</Text>
						</HStack>
						<Text fontSize="xs" color="gray.600" m={0}>Import an analysis into context</Text>
					</VStack>
				</MenuItem>

				{/* Analysis Run — coming soon */}
				<MenuItem py={3} px={3} _hover={{ bg: "purple.50" }} _focus={{ bg: "purple.50" }}>
					<VStack align="stretch" spacing={1} w="100%">
						<HStack spacing={2}>
							<Icon as={MdSchedule} boxSize={5} color="purple.600" />
							<Text fontWeight="600" fontSize="sm" m={0}>Analysis Run</Text>
							<Box as="span" display="inline-flex" alignItems="center" gap={1} px={1.5} py={0.5} borderRadius="sm" bg="purple.50" fontSize="xs" color="purple.700" fontWeight="500">
								<Icon as={FaStar} boxSize={3} />
								Coming soon
							</Box>
						</HStack>
						<Text fontSize="xs" color="gray.600" m={0}>Import the run of a specific analysis</Text>
					</VStack>
				</MenuItem>
			</MenuList>

			{/* ── Learn modal ───────────────────────────────────────────────────── */}
			<Modal
				isOpen={isLearnOpen}
				onClose={handleLearnClose}
				size="3xl"
				isCentered
				initialFocusRef={newInstructionRef}
			>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader pb={3}>
						<HStack spacing={4}>
							<Icon as={GiBrain} boxSize={9} color="purple.600" />
							<Text m={0} fontSize="xl" fontWeight="600">Anko's Learnings</Text>
						</HStack>
					</ModalHeader>
					<ModalCloseButton isDisabled={isSaving} />
					<ModalBody>
						<VStack spacing={5} align="stretch">

							{/* ── New instruction ── */}
							<Box>
								<Text fontSize="md" fontWeight="600" color="gray.700" mb={1}>
									Teach Anko something new
								</Text>
								<Text fontSize="sm" color="gray.600" mb={2}>
									Add a nuance or instruction you want Anko to follow when analysing your data.
								</Text>
								<Textarea
									ref={newInstructionRef}
									value={newInstruction}
									onChange={e => setNewInstruction(e.target.value)}
									placeholder="e.g. Always compare metrics year-over-year. Exclude outliers above 3 standard deviations…"
									rows={4}
									resize="none"
									fontSize="md"
									isDisabled={isSaving}
									_focus={{ borderColor: "purple.400", boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)" }}
								/>
							</Box>

							{/* ── Warning banner ── */}
							<HStack
								bg="yellow.50"
								border="1px solid"
								borderColor="yellow.200"
								borderRadius="md"
								p={3}
								spacing={2}
								alignItems="center"
							>
								<Icon as={MdWarning} color="yellow.500" boxSize={5} flexShrink={0} />
								<Text fontSize="sm" color="yellow.800" fontWeight="600" m={0}>
									New teachings apply to new chats only. They will not take effect in the current chat.
								</Text>
							</HStack>

							<Divider my={0} />

							{/* ── Existing instructions (collapsible) ── */}
							<Box>
								<HStack justify="space-between">
									<Button
										variant="ghost"
										size="sm"
										px={0}
										color="gray.600"
										fontWeight="600"
										leftIcon={<Icon as={isExistingOpen ? MdExpandLess : MdExpandMore} boxSize={6} />}
										_hover={{ bg: "transparent", color: "purple.600" }}
										onClick={() => setIsExistingOpen(v => !v)}
										fontSize="md"
									>
										View existing instructions
									</Button>
									{!isEditingExisting && (
										<Button
											size="xs"
											variant="outline"
											colorScheme="purple"
											leftIcon={<Icon as={MdLock} boxSize={3.5} />}
											onClick={() => { setIsExistingOpen(true); !isSaving && setIsEditingExisting(true); }}
											isDisabled={isSaving}
										>
											Locked · Click to edit
										</Button>
									)}
								</HStack>

								<Collapse in={isExistingOpen} animateOpacity>
									<Box mt={2}>
										{isEditingExisting ? (
											<Textarea
												value={editedExisting}
												onChange={e => setEditedExisting(e.target.value)}
												rows={7}
												resize="none"
												fontSize="sm"
												borderColor="purple.300"
												_focus={{ borderColor: "purple.500", boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)" }}
												isDisabled={isSaving}
											/>
										) : (
											<Box
												bg="gray.50"
												border="1px solid"
												borderColor="gray.200"
												borderRadius="md"
												p={3}
												minH="120px"
												fontSize="sm"
												color={existingInstructions ? "gray.800" : "gray.400"}
												whiteSpace="pre-wrap"
												wordBreak="break-word"
											>
												{existingInstructions || "No instructions yet."}
											</Box>
										)}
									</Box>
								</Collapse>
							</Box>

						</VStack>
					</ModalBody>
					<ModalFooter>
						<HStack spacing={3} w="100%" justify="space-between">
							<Box>
								{isEditingExisting && (
									<Button
										size="sm"
										variant="ghost"
										colorScheme="gray"
										leftIcon={<Icon as={MdUndo} boxSize={4} />}
										onClick={() => {
											setEditedExisting(existingInstructions);
											setIsEditingExisting(false);
										}}
										isDisabled={isSaving}
									>
										Discard edits
									</Button>
								)}
							</Box>
							<HStack spacing={3}>
								<Button variant="ghost" onClick={handleLearnClose} isDisabled={isSaving}>
									Cancel
								</Button>
								<Button
									colorScheme="purple"
									onClick={handleSave}
									isLoading={isSaving}
									loadingText="Saving…"
									isDisabled={!newInstruction.trim() && !isEditingExisting}
									fontWeight="700"
								>
									Save & Open New Chat
								</Button>
							</HStack>
						</HStack>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* ── Analysis modal ────────────────────────────────────────────────── */}
			<Modal isOpen={isAnalysisOpen} onClose={onAnalysisClose} size="3xl" isCentered scrollBehavior="inside" returnFocusOnClose={false}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>
						<HStack spacing={2}>
							<Icon as={FaClipboardList} boxSize={7} color="purple.600" />
							<Text m={0}>Select an Analysis</Text>
						</HStack>
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody px={4} py={0}>
						<VStack spacing={4} align="stretch">
							<Text fontSize="sm" color="gray.600" mb={0}>
								This analysis will be imported into this conversation.
							</Text>
							<InputGroup size="sm">
								<InputLeftElement pointerEvents="none">
									<SearchIcon color="gray.400" />
								</InputLeftElement>
								<Input
									placeholder="Search analyses…"
									value={jobSearch}
									onChange={e => setJobSearch(e.target.value)}
									bg="gray.50"
									borderColor="gray.200"
									_focus={{ borderColor: "purple.400", boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)", bg: "white" }}
								/>
							</InputGroup>
							{isFetchingJobs ? (
								<HStack justify="center" py={6}>
									<Spinner size="md" color="purple.500" />
								</HStack>
							) : filteredJobs.length === 0 ? (
								<Text fontSize="sm" color="gray.500" textAlign="center" py={6}>
									{jobSearch ? "No analyses match your search." : "No analyses found."}
								</Text>
							) : (
								<VStack spacing={0} align="stretch" divider={<Divider />}>
									{filteredJobs.map((job, idx) => {
										const createdDate = job.created_date_ms
											? new Date(job.created_date_ms).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
											: null;
										return (
											<Box
												key={job.id ?? idx}
												px={3}
												py={3}
												cursor="pointer"
												borderRadius="md"
												_hover={{ bg: "purple.50" }}
												onClick={() => handleJobSelect(job)}
											>
												<HStack justify="space-between" align="flex-start">
													<Text fontSize="sm" fontWeight="600" color="gray.800" m={0}>
														{job.name || "Untitled"}
													</Text>
													{job.has_active_schedule && (
														<HStack spacing={1} flexShrink={0}>
															<Box w={2} h={2} borderRadius="full" bg="green.400" />
															<Text fontSize="xs" fontWeight="600" color="green.600" m={0}>Live</Text>
														</HStack>
													)}
												</HStack>
												{job.description && (
													<Text fontSize="xs" color="gray.500" m={0} mt={0.5} noOfLines={1}>
														{job.description}
													</Text>
												)}
												<HStack spacing={2} mt={1.5} flexWrap="wrap">
													{job.author_name && (
														<Text fontSize="xs" color="gray.400" m={0}>
															By {job.author_name}
														</Text>
													)}
													{job.author_name && createdDate && (
														<Text fontSize="xs" color="gray.300" m={0}>·</Text>
													)}
													{createdDate && (
														<Text fontSize="xs" color="gray.400" m={0}>
															{createdDate}
														</Text>
													)}
												</HStack>
											</Box>
										);
									})}
								</VStack>
							)}
						</VStack>
					</ModalBody>
					<ModalFooter>
						<Button variant="ghost" onClick={onAnalysisClose}>
							Cancel
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
};

export default ChatAddMenuComponent;
